import type {
  GetUnitMembersResponse,
  SupabaseClient,
  UnitType,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"

export type UnitMember = GetUnitMembersResponse[number]

export const createUnitsService = (supabase: SupabaseClient) => {
  return new UnitsService(supabase)
}

class UnitsService {
  log = logger.child({ module: "units/units-service" })

  constructor(private readonly supabase: SupabaseClient) {}

  async getUnitId({ slug, unitType }: { slug: string; unitType: UnitType }) {
    const { data, error } = await this.supabase.rpc("get_unit_id", {
      p_slug: slug,
      p_unit_type: unitType,
    })

    if (error) {
      this.log.error("Error fetching unit id", { error })
      throw error
    }

    return data
  }

  async getUnitById({ unitId }: { unitId: string }) {
    const { data, error } = await this.supabase.rpc("get_unit", {
      p_unit_id: unitId,
    })

    if (error) {
      this.log.error("Error fetching unit by id", { error })
      throw error
    }

    return data
  }

  async getUnitBySlug({
    slug,
    unitType,
  }: {
    slug: string
    unitType: UnitType
  }) {
    const { data, error } = await this.supabase.rpc("get_unit_by_slug", {
      p_slug: slug,
      p_unit_type: unitType,
    })

    if (error) {
      this.log.error("Error fetching unit by slug", { error })
      throw error
    }

    return data
  }

  async getUnitsByEmail({
    email,
    unitType,
  }: {
    email: string
    unitType?: UnitType
  }) {
    const { data, error } = await this.supabase.rpc("get_units_by_email", {
      p_email: email,
      p_unit_type: unitType,
    })

    if (error) {
      this.log.error("Error fetching units by email", { error, email })
      throw error
    }

    return data
  }

  async isPersonalUnit({ unitId }: { unitId: string }) {
    const { data, error } = await this.supabase.rpc("is_personal_unit", {
      p_unit_id: unitId,
    })

    if (error) {
      this.log.error("Error checking if unit is personal", { error })
      throw error
    }

    return data
  }

  async getPersonalAccountUnit() {
    const { data, error } = await this.supabase.rpc("get_personal_unit")

    if (error) {
      this.log.error("Error fetching personal account unit", { error })
      throw error
    }

    return data
  }

  async getUnits({ unitType }: { unitType?: UnitType }) {
    const { data, error } = await this.supabase.rpc("get_units", {
      p_unit_type: unitType,
    })

    if (error) {
      this.log.error("Error fetching units", { error })
      throw error
    }

    return data
  }

  async createUnit({
    unitType,
    name,
    slug,
  }: {
    unitType: UnitType
    name: string
    slug?: string
  }) {
    const { data, error } = await this.supabase.rpc("create_unit", {
      p_unit_type: unitType,
      p_name: name,
      p_slug: slug,
    })

    if (error) {
      this.log.error("Error creating unit", { error })
      throw error
    }

    if (!data) {
      this.log.error("No data returned from create unit")
      throw new Error("No data returned from create unit")
    }

    return data
  }

  async updateUnit({
    unitId,
    name,
    slug,
    pictureUrl,
  }: {
    unitId: string
    name?: string
    slug?: string
    pictureUrl?: string
  }) {
    this.log.debug("Updating unit", { unitId, name, slug, pictureUrl })

    const { data, error } = await this.supabase.rpc("update_unit", {
      p_unit_id: unitId,
      p_name: name,
      p_slug: slug,
      p_picture_url: pictureUrl,
    })

    if (error) {
      this.log.error("Error updating unit", {
        error,
        unitId,
        hasSlug: Boolean(slug),
        hasName: Boolean(name),
        hasPictureUrl: Boolean(pictureUrl),
      })
      throw new Error("Failed to update unit", { cause: error })
    }

    this.log.debug("Unit updated", { unitId, name, slug, pictureUrl })

    return data
  }

  async deleteUnit({ unitId }: { unitId: string }) {
    const { data, error } = await this.supabase.rpc("delete_unit", {
      p_unit_id: unitId,
    })

    if (error) {
      this.log.error("Error deleting unit", { error: error.message, unitId })
      throw error
    }

    return data
  }

  async isUnitMember({ unitId, userId }: { unitId: string; userId: string }) {
    const { data, error } = await this.supabase.rpc("is_unit_member", {
      p_unit_id: unitId,
      p_user_id: userId,
    })

    if (error) {
      this.log.error("Error checking if user is unit member", { error })
      throw error
    }

    return data
  }

  async getUnitMembers({
    unitId,
    limit = 100,
    offset = 0,
  }: {
    unitId: string
    limit?: number
    offset?: number
  }) {
    const { data, error } = await this.supabase.rpc("get_unit_members", {
      p_unit_id: unitId,
      p_limit: limit,
      p_offset: offset,
    })

    if (error) {
      this.log.error("Error fetching unit users", { error })
      throw error
    }

    return data
  }

  async removeUnitMember({
    unitId,
    userId,
  }: {
    unitId: string
    userId: string
  }) {
    const { data, error } = await this.supabase.rpc("remove_unit_member", {
      p_unit_id: unitId,
      p_user_id: userId,
    })

    if (error) {
      this.log.error("Error removing unit member", { error })
      throw error
    }

    return data
  }

  async updateUnitMemberRole({
    unitId,
    userId,
    roleId,
  }: {
    unitId: string
    userId: string
    roleId: number
  }) {
    const { error } = await this.supabase.rpc("update_unit_user_role", {
      p_unit_id: unitId,
      p_user_id: userId,
      p_role_id: roleId,
      p_make_primary_owner: false,
    })

    if (error) {
      this.log.error("Error updating unit member role", {
        error,
        unitId,
        userId,
        roleId,
      })
      throw new Error("Failed to update unit member role", { cause: error })
    }

    this.log.debug("Unit member role updated", { unitId, userId, roleId })
    return
  }
}
